import { t } from "./t";

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;

export type ExpiryTone = "ok" | "warn" | "bad" | "unknown";

/**
 * §B6.5 connection cards show token expiry as relative, colour-coded text. Literal `t()` calls with literal keys
 * only — tests/i18n.test.ts greps for them. `now` is injectable for tests.
 */
export function formatRelativeExpiry(
  iso: string | null,
  now: number = Date.now(),
): { tone: ExpiryTone; text: string } {
  if (iso === null) return { tone: "unknown", text: t("time.unknown") };
  const delta = Date.parse(iso) - now;
  const days = Math.round(Math.abs(delta) / DAY_MS);
  const hours = Math.round(Math.abs(delta) / HOUR_MS);
  // Under an hour, hours round to "0 hours" - say minutes instead (never less than one).
  if (Math.abs(delta) < HOUR_MS) {
    const minutes = Math.max(1, Math.round(Math.abs(delta) / MINUTE_MS));
    return delta < 0
      ? { tone: "bad", text: t("time.minutes_ago", { count: minutes }) }
      : { tone: "warn", text: t("time.in_minutes", { count: minutes }) };
  }
  if (delta < 0) {
    return days >= 1
      ? { tone: "bad", text: t("time.days_ago", { count: days }) }
      : { tone: "bad", text: t("time.hours_ago", { count: hours }) };
  }
  if (delta >= DAY_MS) {
    return { tone: "ok", text: t("time.in_days", { count: days }) };
  }
  return { tone: "warn", text: t("time.in_hours", { count: hours }) };
}

/** "Last refreshed 3 hours ago": plain relative past time, no colour; `iso` is when something already happened. */
export function formatRelativePast(iso: string, now: number = Date.now()): string {
  const delta = Math.max(0, now - Date.parse(iso));
  if (delta < 60_000) return t("time.just_now");
  if (delta < HOUR_MS) return t("time.minutes_past", { count: Math.round(delta / 60_000) });
  if (delta < DAY_MS) return t("time.hours_past", { count: Math.round(delta / HOUR_MS) });
  return t("time.days_past", { count: Math.round(delta / DAY_MS) });
}
